import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { useAuth } from '../context/AuthContext';
import { useEffect, useState } from 'react';
import BillingBanner from './BillingBanner';
import { Link } from 'react-router-dom';

const MainLayout = ({ children, title, subtitle, immersive = false }) => {
  const { user, loading, updateUser } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const updateMobile = () => {
      const nextIsMobile = window.innerWidth < 768;
      setIsMobile(nextIsMobile);
      if (!nextIsMobile) setMobileSidebarOpen(false);
    };
    updateMobile();
    window.addEventListener('resize', updateMobile);
    return () => window.removeEventListener('resize', updateMobile);
  }, []);

  useEffect(() => {
    const handleKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'b' && isMobile) {
        event.preventDefault();
        setMobileSidebarOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isMobile]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F7FA', fontFamily: "'Public Sans', sans-serif" }}>
        <div className="w-8 h-8 border-2 border-slate-300 rounded-full animate-spin" style={{ borderTopColor: '#0D1F3C' }} />
      </div>
    );
  }

  if (!user) {
    return <>{children}</>;
  }

  const sidebarOffset = isMobile ? 0 : 244;
  const showOnboardingBanner = !user.dismissed_onboarding_banner && user.profile_review_status === 'needs_review';

  const dismissOnboardingBanner = async () => {
    const token = localStorage.getItem('token');
    const api = process.env.REACT_APP_BACKEND_URL || '';
    const response = await fetch(`${api}/api/auth/onboarding-banner/dismiss`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) updateUser({ ...user, dismissed_onboarding_banner: true });
  };

  return (
    <div className="bsiq-product-shell min-h-screen flex overflow-hidden" style={{ backgroundColor: '#F5F7FA', fontFamily: "'Public Sans', sans-serif" }}>
      <Sidebar
        isMobile={isMobile}
        mobileOpen={mobileSidebarOpen}
        onMobileToggle={() => setMobileSidebarOpen(prev => !prev)}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />
      <main
        className={`bsiq-app-content flex-1 transition-all duration-200 ${immersive ? 'h-screen overflow-hidden' : 'overflow-y-auto'}`}
        style={{ backgroundColor: '#F5F7FA', marginLeft: `${sidebarOffset}px` }}
      >
        <TopBar title={title} subtitle={subtitle} sidebarOffset={sidebarOffset} />
        {immersive ? (
          <div className="flex h-screen flex-col pt-16">
            <BillingBanner inline />
            <div className="min-h-0 flex-1">{children}</div>
          </div>
        ) : (<>
          <BillingBanner />
          <div className="bsiq-product-canvas pt-24 px-4 pb-12 sm:px-6 xl:px-8 2xl:px-10">
          {showOnboardingBanner && (
            <div className="mb-6 flex flex-col gap-4 border border-slate-200 bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between" data-testid="onboarding-review-banner">
              <div>
                <p className="text-sm font-bold text-[#0D1F3C]">Review your company profile</p>
                <p className="mt-1 text-sm text-slate-600">We created a starting profile from public federal award data. Confirm your capabilities to improve opportunity matching.</p>
              </div>
              <div className="flex shrink-0 items-center gap-4">
                <button type="button" onClick={dismissOnboardingBanner} className="text-sm font-semibold text-slate-500">Set Up Later</button>
                <Link to="/company" className="rounded-md bg-[#0D1F3C] px-4 py-2 text-sm font-bold text-white">Review Company Profile</Link>
              </div>
            </div>
          )}
          <div className="bsiq-product-page" data-testid="product-page-canvas">
            {children}
          </div>
          </div>
        </>)}
      </main>
    </div>
  );
};

export default MainLayout;
